import { Box, Button, Divider } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { PAGE_SIZES, usePersistedPagination } from "../utils/usePersistedPagination";
import FilterMultiSelectWithSearch, { exactMulti } from "./FilterMultiSelectWithSearch";

export default function UsersTab({ users = [], paginationKey }) {
  const [pagination, setPagination] = usePersistedPagination(paginationKey);
  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "User",
        minSize: 160,
        size: 160,
        enableGlobalFilter: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        accessorKey: "job_title",
        header: "Job Title",
        minSize: 160,
        size: 160,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
        enableGlobalFilter: false,
      },
      {
        id: "business_phone",
        header: "Business Phone",
        minSize: 160,
        size: 160,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        accessorKey: "email_address",
        header: "Email",
        minSize: 300,
        size: 300,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
        enableGlobalFilter: false,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    /* ── Data & identity ─────────────────────────────────────────────── */
    columns,
    data: users ?? [],
    getRowId: (row) => String(row.id),

    /* ── Feature toggles ─────────────────────────────────────────────── */
    enableColumnActions: false,
    enableSorting: false,
    enableRowSelection: false,
    enablePagination: true,
    enableColumnResizing: true,
    layoutMode: "grid",

    /* ── Filtering & search ──────────────────────────────────────────── */
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableFilterMatchHighlighting: true, // highlight matches in cells
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    positionGlobalFilter: "left",
    muiSearchTextFieldProps: {
      placeholder: "Search users by name",
    },

    /* ── Pagination ──────────────────────────────────────────────────── */
    state: { pagination },
    onPaginationChange: setPagination,
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: PAGE_SIZES },
    muiBottomToolbarProps: {
      sx: {
        minHeight: "auto",
        "& > .MuiBox-root > .MuiBox-root": { position: "relative", top: "auto", right: "auto" },
        "& .MuiTablePagination-root": { py: 0 },
      },
    },

    /* ── Initial table state ─────────────────────────────────────────── */
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      showGlobalFilter: true,
      showColumnFilters: true, // open filters panel by default
      columnPinning: { left: ["name"] },
    },

    /* ── Toolbar layout (right cluster) ──────────────────────────────── */
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Filters cluster */}
        <Button
          variant="outlined"
          onClick={() => {
            table.resetColumnFilters();
            table.resetGlobalFilter();
          }}
        >
          Clear Filters
        </Button>
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* View controls */}
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ShowHideColumnsButton table={table} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleDensePaddingButton table={table} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),

    /* ── Misc ───────────────────────────────────────────────────────── */
    positionToolbarAlertBanner: "none",
  });

  return (
    <Box>
      <MaterialReactTable table={table} />
    </Box>
  );
}
